import * as React from 'react';
import type { TFunction } from 'i18next';
import { MediaTypeEnum } from '@ooh/contracts';
import type { FilterFieldLabels } from '../../features/media-units/lib/filter-fields';

/**
 * Column-parity labels (catalog-column-filter-parity) — extracted so
 * `useMapFilterLabels` stays within the file's max-lines-per-function budget.
 * These are now genuinely live on /map too (`buildFilterFields` is called
 * with `includeCatalogOnlyFields: true` by `use-map-pins-data.ts` —
 * map-catalog-filter-parity). `status`/`supplierEmail`/`supplierPhone`/
 * `supplierContactInfo` were removed entirely (CU-86cb1nq7h) — no longer
 * labels to carry at all.
 */
function buildColumnParityMapLabels(t: TFunction<'map'>) {
  return {
    notes: t('filters.fields.notes'),
    roadNumber: t('filters.fields.roadNumber'),
  };
}

/**
 * Builds the canonical `FilterFieldLabels` object for the `/map` route, sourced
 * entirely from the `map` i18n namespace (`filters.fields.*` and `legend.types.*`).
 *
 * The catalog's `useFilterLabels` reads `inventory`-namespaced keys; the map route
 * mirrors the same field set into its own `map` namespace instead of importing
 * `inventory` translations, per the interactive-map filter-convergence design.
 */
export function useMapFilterLabels(t: TFunction<'map'>): FilterFieldLabels {
  return React.useMemo<FilterFieldLabels>(
    () => ({
      region: t('filters.fields.region'),
      county: t('filters.fields.county'),
      agglomeration: t('filters.fields.agglomeration'),
      city: t('filters.fields.city'),
      formatCode: t('filters.fields.formatCode'),
      mediaType: t('filters.fields.mediaType'),
      supplierId: t('filters.fields.supplierId'),
      lighting: t('filters.fields.lighting'),
      // supplierNumber IS rendered on /map (shared `buildStandardFields`,
      // not gated behind includeCatalogOnlyFields) — a real label, not dead.
      supplierNumber: t('filters.fields.supplierNumber'),
      grwId: t('filters.fields.grwId'),
      searchPlaceholder: t('filters.searchPlaceholder'),
      mediaTypeOptionLabels: Object.fromEntries(
        MediaTypeEnum.options.map((mt) => [mt, t(`legend.types.${mt}`)]),
      ),
      location: t('filters.fields.location'),
      locationSearchPlaceholder: t('filters.fields.locationSearchPlaceholder'),
      locationInfoHint: t('filters.fields.locationInfoHint'),
      locationInfoHintLabel: t('filters.fields.locationInfoHintLabel'),
      administrativeArea: t('filters.fields.administrativeArea'),
      // Catalog-only fields (CU-86cataqt9) — now genuinely rendered on /map too
      // (includeCatalogOnlyFields: true, map-catalog-filter-parity).
      material: t('filters.fields.material'),
      multiformat: t('filters.fields.multiformat'),
      tags: t('filters.fields.tags'),
      address: t('filters.fields.address'),
      area: t('filters.fields.area'),
      width: t('filters.fields.width'),
      height: t('filters.fields.height'),
      audience: t('filters.fields.audience'),
      catalogPrice: t('filters.fields.catalogPrice'),
      rent: t('filters.fields.rent'),
      exposureCost: t('filters.fields.exposureCost'),
      additionalCosts: t('filters.fields.additionalCosts'),
      discountedPrice: t('filters.fields.discountedPrice'),
      lastMinutePrice: t('filters.fields.lastMinutePrice'),
      starterPrice: t('filters.fields.starterPrice'),
      productionCost: t('filters.fields.productionCost'),
      installationCost: t('filters.fields.installationCost'),
      dismantlingCost: t('filters.fields.dismantlingCost'),
      clientProduction: t('filters.fields.clientProduction'),
      clientInstallation: t('filters.fields.clientInstallation'),
      clientDismantling: t('filters.fields.clientDismantling'),
      clientAdditionalFees: t('filters.fields.clientAdditionalFees'),
      ...buildColumnParityMapLabels(t),
    }),
    [t],
  );
}
